import { useCallback, useEffect } from "react"

import { Feature } from "@turf/turf"
import { useMatch } from "react-router-dom"

import {
    BASE_MAP_ID,
    useMapContext,
} from "@l2r-front/l2r-map"
import { NavigationOrigins } from "@l2r-front/l2r-utils"

import { useNetworksDispatchContext, useNetworksStateContext } from "../../contexts/NetworksContext"

import { BaseNetworkReferentialLayer, BaseNetworkReferentialLayerProps } from "../BaseNetworkReferentialLayer"

type LinearLocation = {
    road: string,
    start?: number,
    end?: number,
}

type NetworkReferentialLayerProps = BaseNetworkReferentialLayerProps & {
    module: string,
    roadToHide?: string,
    processNavigation?: (_feature: Feature | null, _linearLocation: LinearLocation, _origin?: typeof NavigationOrigins[keyof typeof NavigationOrigins]) => void,
}

export function NetworkReferentialLayer(props: NetworkReferentialLayerProps) {

    const {
        mapId = BASE_MAP_ID,
        module,
        roadToHide,
        processNavigation,
        ...layerProps
    } = props

    const matchRoad = useMatch(":slugId/:module/:subModule/:road/*")
    const matchDetail = useMatch(":slugId/:module/:subModule/:road/:detailUuid/*")

    const isCampaignModule = matchRoad?.params?.module === "campaign"

    const isSegmentView = !!matchRoad && !isCampaignModule
    const road = (matchRoad?.params?.road && !isCampaignModule) ? decodeURIComponent(matchRoad.params.road) : null
    const isDetailView = matchDetail?.params?.detailUuid && !isCampaignModule
    const { selectedNetwork, selectedSubNetworkSlugs } = useNetworksStateContext()

    const availableSubNetworksSlugsForSubModule = selectedSubNetworkSlugs.map(subNetwork => {
        return selectedNetwork?.["children"].find(child => child.slug === subNetwork)
    })
        ?.filter(child => child.modules?.includes(module))
        ?.map(sub => sub.slug) || []

    const { setRoadSearched } = useNetworksDispatchContext()

    const { selectedLinearLocations } = useNetworksStateContext()

    const [mapState, mapDispatch] = useMapContext()
    const { getCurrentMapStyle } = mapState
    const { setMapPin, setSearchRoadCallback, storeMapBoundingBox } = mapDispatch

    useEffect(() => {
        setSearchRoadCallback((searchedRoad: string) => {
            if (processNavigation) {
                processNavigation(null, { road: searchedRoad })
            }
        })

        return () => setSearchRoadCallback(null)
    }, [
        setSearchRoadCallback,
        processNavigation])

    const onReferentialClick = useCallback((feature: Feature) => {
        if (processNavigation && feature.properties && feature.properties["linearLocation"]) {
            const linearLocation = JSON.parse(feature.properties["linearLocation"])
            if (linearLocation?.[0]?.road !== road) {
                const navigationOrigin = isDetailView ? NavigationOrigins.ROAD_FROM_DETAIL
                    : isSegmentView ? NavigationOrigins.ROAD_FROM_ROAD
                        : NavigationOrigins.ROAD_FROM_NETWORK
                setRoadSearched(null)
                setMapPin()
                storeMapBoundingBox()
                processNavigation(feature, linearLocation[0], navigationOrigin)
            }
        }
    }, [isDetailView, isSegmentView, processNavigation, setRoadSearched, setMapPin, storeMapBoundingBox, road])

    return (
        <BaseNetworkReferentialLayer
            onClickOnFeature={!isCampaignModule ? onReferentialClick : undefined}
            mapId={mapId}
            mapStyle={getCurrentMapStyle(mapId)}
            roadToHide={roadToHide}
            selectedRoad={selectedLinearLocations?.[0]?.road || undefined}
            subNetworks={availableSubNetworksSlugsForSubModule ?? null}
            {...layerProps}
        />
    )
}
